'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type ClientHeadProps = {
  children: ReactNode;
  /**
   * When true, skips portaling on the first client render so server-rendered
   * head tags are not duplicated. Subsequent updates (e.g. client navigation)
   * are portaled into document.head.
   */
  deferInitialPortal?: boolean;
};

/**
 * Renders `<title>`, `<meta>`, `<link>`, and `<script>` from client components under the App Router.
 * `next/head` only works with the Pages router; portaling into `document.head` preserves equivalent behavior.
 */
export default function ClientHead({
  children,
  deferInitialPortal = false,
}: ClientHeadProps): ReactNode {
  const [mounted, setMounted] = useState(false);
  const [shouldPortal, setShouldPortal] = useState(!deferInitialPortal);
  const childrenRef = useRef(children);
  const skipInitialChildrenChange = useRef(deferInitialPortal);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !deferInitialPortal) {
      return;
    }
    if (skipInitialChildrenChange.current) {
      skipInitialChildrenChange.current = false;
      childrenRef.current = children;
      return;
    }
    if (childrenRef.current !== children) {
      childrenRef.current = children;
      setShouldPortal(true);
    }
  }, [mounted, children, deferInitialPortal]);

  if (!mounted || !shouldPortal) {
    return null;
  }

  return createPortal(children, document.head);
}
